import React from 'react';
import { Mail, Phone, MapPin, Linkedin, Globe, Award, Briefcase, GraduationCap, BookOpen } from 'lucide-react';
import ProfilePictureFrame from '../ProfilePictureFrame';
import { getFontStyle, getHeaderFontStyle } from '../../utils/fontSizeHelper';

const ModernTemplate = ({ resume }) => {
  const highlightColor = resume.highlight_color || '#3b82f6';
  
  // Content visibility helpers
  const hiddenContent = resume?.hidden_content || { sections: {}, items: {} };
  
  const isSectionHidden = (sectionKey) => {
    return hiddenContent.sections?.[sectionKey] === true;
  };
  
  const isItemHidden = (sectionKey, index) => {
    const itemKey = `${sectionKey}_${index}`;
    return hiddenContent.items?.[itemKey] === true;
  };
  
  // Filter array items based on visibility settings
  const filterVisibleItems = (items, sectionKey) => {
    if (!Array.isArray(items)) return [];
    return items.filter((_, index) => !isItemHidden(sectionKey, index));
  };
  
  // Get visible items for each section
  const visibleExperience = isSectionHidden('experience') ? [] : filterVisibleItems(resume.experience, 'experience');
  const visibleEducation = isSectionHidden('education') ? [] : filterVisibleItems(resume.education, 'education');
  const visibleTraining = isSectionHidden('training') ? [] : filterVisibleItems(resume.training, 'training');
  const visibleSkills = isSectionHidden('skills') ? [] : filterVisibleItems(resume.skills, 'skills');
  const visibleCertifications = isSectionHidden('certifications') ? [] : filterVisibleItems(resume.certifications, 'certifications');
  const visibleLanguages = isSectionHidden('languages') ? [] : filterVisibleItems(resume.languages, 'languages');
  
  // Check visibility for single-value sections
  const showPhoto = !isSectionHidden('photo') && resume.photo_url;
  const showBanner = !isSectionHidden('banner') && resume.banner_url;
  const showContact = !isSectionHidden('contact');
  const showProfessionalSummary = !isSectionHidden('professional_summary') && resume.professional_summary;
  
  return (
    <div
      className="bg-white shadow-lg max-w-4xl mx-auto print:shadow-none print:mx-0 print:my-0 border"
      style={{ borderColor: highlightColor }}
    >
      {/* Header with Banner */}
      {showBanner && (
        <div
          className="h-32 bg-gradient-to-r from-primary-500 to-primary-700"
          style={{ backgroundImage: `url(${resume.banner_url})`, backgroundSize: 'cover' }}
        />
      )}

      {/* Profile Section */}
      <div className="px-8 py-6 border-b border-gray-200" style={getFontStyle(resume, 'header')}>
        <div className="flex items-start gap-6">
          {showPhoto && (
            <ProfilePictureFrame 
              photoUrl={resume.photo_url}
              frameOption={resume.profile_frame}
              size="w-24 h-24"
              className={showBanner ? "-mt-12" : ""}
            />
          )}
          <div className="flex-1">
            <h1 className="text-4xl font-bold text-gray-900 mb-2" style={getHeaderFontStyle(resume, 'header')}>{resume.title}</h1>
            {resume.headline && (
              <p className="text-xl font-medium mb-3" style={{ color: highlightColor }}>{resume.headline}</p>
            )}
            {resume.target_role && (
              <p className="text-lg text-gray-700 mb-2">
                {resume.target_role}
                {resume.seniority_level && ` • ${resume.seniority_level}`}
              </p>
            )}

            {/* Contact Information */}
            {showContact && (
              <div className="flex flex-wrap gap-4 text-sm text-gray-600 mt-3">
                {resume.contact_email && (
                  <div className="flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    <span>{resume.contact_email}</span>
                  </div>
                )}
                {resume.contact_phone && (
                  <div className="flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    <span>{resume.contact_phone}</span>
                  </div>
                )}
                {resume.location && (
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    <span>{resume.location}</span>
                  </div>
                )}
                {resume.linkedin_url && (
                  <a href={resume.linkedin_url} className="flex items-center gap-1 hover:underline" style={{ color: highlightColor }}>
                    <Linkedin className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </a>
                )}
                {resume.website_url && (
                  <a href={resume.website_url} className="flex items-center gap-1 hover:underline" style={{ color: highlightColor }}>
                    <Globe className="w-4 h-4" />
                    <span>Website</span>
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-8 py-6">
        {/* Professional Summary */}
        {showProfessionalSummary && (
          <section className="mb-8" style={getFontStyle(resume, 'summary')}>
            <h2 className="text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2" style={getHeaderFontStyle(resume, 'summary')}>
              <div className="w-1 h-6" style={{ backgroundColor: highlightColor }}></div>
              Professional Summary
            </h2>
            <p className="text-gray-700 leading-relaxed">{resume.professional_summary}</p>
          </section>
        )}

        {/* Core Skills - two-column bullet list */}
        {resume.core_skills && resume.core_skills.length > 0 && (
          <section className="mb-8" style={getFontStyle(resume, 'core_skills')}>
            <h2 className="text-2xl font-bold text-gray-900 mb-3 flex items-center gap-2" style={getHeaderFontStyle(resume, 'core_skills')}>
              <div className="w-1 h-6" style={{ backgroundColor: highlightColor }}></div>
              Core Skills
            </h2>
            <div className="grid grid-cols-2 gap-x-8 gap-y-1">
              {resume.core_skills.slice(0, 10).map((skill, index) => (
                <div key={skill.id || index} className="flex items-start gap-2 text-gray-700">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: highlightColor }}></span>
                  <span>{skill.name || skill}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Experience */}
        {visibleExperience && visibleExperience.length > 0 && (
          <section className="mb-8" style={getFontStyle(resume, 'experience')}>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={getHeaderFontStyle(resume, 'experience')}>
              <div className="w-1 h-6" style={{ backgroundColor: highlightColor }}></div>
              <Briefcase className="w-6 h-6" />
              Experience
            </h2>
            <div className="space-y-8">
              {(() => {
                // Group experiences by organization
                const groupedExperiences = {};
                visibleExperience.forEach((exp, idx) => {
                  const company = exp.company || 'Unknown';
                  if (!groupedExperiences[company]) {
                    groupedExperiences[company] = {
                      company: exp.company,
                      organization_logo: exp.organization_logo,
                      location_type: exp.location_type,
                      employment_type: exp.employment_type,
                      experiences: []
                    };
                  }
                  groupedExperiences[company].experiences.push({ exp, index: idx });
                });

                const formatDate = (month, year) => {
                  if (!month || !year) return '';
                  return `${month} ${year}`;
                };

                return Object.values(groupedExperiences).map((group, groupIndex) => (
                  <div key={groupIndex} className="relative">
                    {/* Organization header with logo */}
                    <div className="relative mb-4">
                      {/* Organization logo */}
                      <div className="absolute left-0 top-0">
                        <div className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden bg-gray-100 border border-gray-200 flex items-center justify-center">
                          {group.organization_logo ? (
                            <img src={group.organization_logo} alt={`${group.company} logo`} className="w-full h-full object-contain" />
                          ) : (
                            <Briefcase className="w-6 h-6 sm:w-7 sm:h-7 text-gray-400" />
                          )}
                        </div>
                      </div>
                      {/* Organization name and details */}
                      <div style={{ minHeight: '3.5rem', paddingLeft: '6rem' }}>
                        <p className="text-base font-bold text-gray-900">{group.company}</p>
                      </div>
                    </div>
                    {/* Roles within organization */}
                    <div className="space-y-5 relative" style={{ marginLeft: '6rem' }}>
                      {/* Vertical line for multiple roles */}
                      {group.experiences.length > 1 && (
                        <div 
                          className="absolute left-0 w-0.5 bg-gray-300" 
                          style={{ 
                            left: '-4.0625rem', 
                            top: '0.625rem',
                            height: 'calc(100% - 1.25rem)'
                          }}
                        ></div>
                      )}
                      {group.experiences.map(({ exp, index }) => {
                        const startDate = exp.start_month && exp.start_year 
                          ? formatDate(exp.start_month, exp.start_year)
                          : exp.start_date || '';
                        const endDate = exp.is_current 
                          ? 'Present'
                          : (exp.end_month && exp.end_year 
                            ? formatDate(exp.end_month, exp.end_year)
                            : exp.end_date || 'Present');
                        const dateRange = startDate ? `${startDate}${endDate ? ` - ${endDate}` : ''}` : '';

                        return (
                          <div key={index} className="flex gap-3 relative">
                            {/* Dot on vertical line for multiple roles */}
                            {group.experiences.length > 1 && (
                              <div className="absolute w-2 h-2 rounded-full bg-gray-400" style={{ left: '-4.25rem', top: '0.375rem' }} aria-hidden />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-base font-semibold text-gray-900">{exp.title}</p>
                              <div className="text-sm text-gray-600 mt-0.5 space-y-0.5">
                                {dateRange && <div>{dateRange}</div>}
                                {exp.location && <div>{exp.location}</div>}
                              </div>
                              {exp.description && (() => {
                                const lines = exp.description.split('\n').filter(l => l.trim());
                                const bulletRegex = /^[•\-\*▪▸►◆○●·⬥]\s*/;
                                const hasBullets = lines.some(l => bulletRegex.test(l.trim()));
                                if (hasBullets) {
                                  return (
                                    <ul className="mt-2 space-y-1.5">
                                      {lines.map((line, i) => {
                                        const trimmed = line.trim();
                                        const text = trimmed.replace(bulletRegex, '');
                                        return (
                                          <li key={i} className="text-gray-700 flex items-start text-sm">
                                            <span className="mr-2 flex-shrink-0" style={{ color: highlightColor }}>•</span>
                                            <span>{text || trimmed}</span>
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  );
                                }
                                return (
                                  <div className="mt-2">
                                    <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap" style={{ lineHeight: '1.75' }}>
                                      {exp.description}
                                    </p>
                                  </div>
                                );
                              })()}
                              {exp.achievements && exp.achievements.length > 0 && (
                                <ul className="mt-2 space-y-1.5">
                                  {exp.achievements.map((achievement, i) => (
                                    <li key={i} className="text-gray-700 flex items-start text-sm">
                                      <span className="mr-2 flex-shrink-0" style={{ color: highlightColor }}>•</span>
                                      <span>{achievement}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                              {exp.skills && exp.skills.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                  {exp.skills.filter(skill => skill && skill.trim()).map((skill, skillIndex) => (
                                    <span key={skillIndex} className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm">
                                      {skill}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ));
              })()}
            </div>
          </section>
        )}

        {/* Education */}
        {visibleEducation && visibleEducation.length > 0 && (
          <section className="mb-8" style={getFontStyle(resume, 'education')}>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={getHeaderFontStyle(resume, 'education')}>
              <div className="w-1 h-6" style={{ backgroundColor: highlightColor }}></div>
              <GraduationCap className="w-6 h-6" />
              Education
            </h2>
            <div className="space-y-4">
              {visibleEducation.map((edu, index) => {
                // Format dates from month/year format
                const formatDate = (month, year) => {
                  if (!month || !year) return '';
                  return `${month} ${year}`;
                };
                
                const startDate = edu.start_month && edu.start_year 
                  ? formatDate(edu.start_month, edu.start_year)
                  : edu.start_date || '';
                const endDate = edu.is_expected
                  ? (edu.end_month && edu.end_year 
                    ? `Expected ${formatDate(edu.end_month, edu.end_year)}`
                    : 'Expected')
                  : (edu.end_month && edu.end_year 
                    ? formatDate(edu.end_month, edu.end_year)
                    : edu.end_date || 'Present');
                
                return (
                  <div key={index} className="relative mb-4">
                    <div className="absolute left-0 top-0">
                      <div className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden bg-gray-100 border border-gray-200 flex items-center justify-center">
                        {edu.institution_logo ? (
                          <img src={edu.institution_logo} alt={`${edu.institution} logo`} className="w-full h-full object-contain" />
                        ) : (
                          <GraduationCap className="w-6 h-6 sm:w-7 sm:h-7 text-gray-400" />
                        )}
                      </div>
                    </div>
                    <div style={{ paddingLeft: '5rem' }}>
                      <h3 className="text-xl font-semibold text-gray-900">{edu.degree}</h3>
                      <p className="text-lg font-medium text-gray-700">{edu.institution}</p>
                      <p className="text-sm text-gray-600 mb-2">
                        {startDate} - {endDate}
                        {edu.gpa && ` • GPA: ${edu.gpa}`}
                      </p>
                      {edu.location && (
                        <p className="text-sm text-gray-600 mb-2">{edu.location}</p>
                      )}
                      {edu.description && (
                        <p className="text-gray-700 mt-1">{edu.description}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Training */}
        {visibleTraining && visibleTraining.length > 0 && (
          <section className="mb-8" style={getFontStyle(resume, 'training')}>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={getHeaderFontStyle(resume, 'training')}>
              <div className="w-1 h-6" style={{ backgroundColor: highlightColor }}></div>
              <BookOpen className="w-6 h-6" />
              Training & Professional Development
            </h2>
            <div className="space-y-4">
              {visibleTraining.map((training, index) => (
                <div key={index} className="border-l-2 pl-4" style={{ borderColor: `${highlightColor}80` }}>
                  <h3 className="text-xl font-semibold text-gray-900">{training.name}</h3>
                  <p className="text-lg font-medium" style={{ color: highlightColor }}>{training.provider}</p>
                  <p className="text-sm text-gray-600">
                    {training.start_date} - {training.end_date || 'Present'}
                    {training.location && ` • ${training.location}`}
                  </p>
                  {training.description && (
                    <p className="text-gray-700 mt-1">{training.description}</p>
                  )}
                  {training.certificate_url && (
                    <a href={training.certificate_url} className="hover:underline text-sm mt-1 inline-block" style={{ color: highlightColor }}>
                      View Certificate
                    </a>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Skills */}
        {visibleSkills && visibleSkills.length > 0 && (
          <section className="mb-8" style={getFontStyle(resume, 'skills')}>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={getHeaderFontStyle(resume, 'skills')}>
              <div className="w-1 h-6" style={{ backgroundColor: highlightColor }}></div>
              Skills
            </h2>
            <div className="flex flex-wrap gap-2">
              {visibleSkills.map((skill, index) => (
                <span
                  key={index}
                  className="px-3 py-1 rounded-full text-sm font-medium text-white"
                  style={{ backgroundColor: highlightColor }}
                >
                  {skill}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Projects section removed per user request - hidden for all users */}

        {/* Certifications */}
        {visibleCertifications && visibleCertifications.length > 0 && (
          <section className="mb-8" style={getFontStyle(resume, 'certifications')}>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={getHeaderFontStyle(resume, 'certifications')}>
              <div className="w-1 h-6" style={{ backgroundColor: highlightColor }}></div>
              <Award className="w-6 h-6" />
              Certifications
            </h2>
            <div className="space-y-3">
              {visibleCertifications.map((cert, index) => (
                <div key={index} className="flex items-start">
                  {/* Certification Logo/Icon */}
                  <div className="relative w-10 h-10 rounded-lg overflow-hidden bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0 mr-3 mt-0.5">
                    {cert.certification_logo ? (
                      <img src={cert.certification_logo} alt={`${cert.name} logo`} className="w-full h-full object-contain" />
                    ) : (
                      <Award className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{cert.name}</p>
                    <p className="text-sm text-gray-600">
                      {cert.issuer}
                      {(cert.issue_month && cert.issue_year) && ` • ${cert.issue_month} ${cert.issue_year}`}
                      {!cert.issue_month && !cert.issue_year && cert.date && ` • ${cert.date}`}
                      {(cert.expiration_month && cert.expiration_year) && ` - ${cert.expiration_month} ${cert.expiration_year}`}
                    </p>
                    {cert.credential_id && (
                      <p className="text-xs text-gray-500 mt-1">ID: {cert.credential_id}</p>
                    )}
                    {cert.credential_url && (
                      <a href={cert.credential_url} target="_blank" rel="noopener noreferrer" className="text-sm mt-1 inline-block hover:underline" style={{ color: highlightColor }}>
                        Show credential
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Languages */}
        {visibleLanguages && visibleLanguages.length > 0 && (
          <section className="mb-8" style={getFontStyle(resume, 'languages')}>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={getHeaderFontStyle(resume, 'languages')}>
              <div className="w-1 h-6" style={{ backgroundColor: highlightColor }}></div>
              Languages
            </h2>
            <div className="flex flex-wrap gap-4">
              {visibleLanguages.map((lang, index) => (
                <div key={index}>
                  <span className="font-semibold text-gray-900">{lang.name}</span>
                  <span className="text-gray-600"> - {lang.proficiency}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ModernTemplate;
